import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';

// Custom German number formatting function
const formatGermanNumber = (num: number): string => {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

// Format date to DD.MM.YY format
const formatDateToDDMMYY = (dateString: string): string => {
  const date = new Date(dateString);
  const year = date.getFullYear().toString().slice(-2);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  return `${day}.${month}.${year}`;
};

interface Registration {
  id: string;
  courseId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  website?: string;
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  invoicingCompany?: string;
  invoicingStreet?: string;
  invoicingHouseNumber?: string;
  invoicingPostalCode?: string;
  invoicingCity?: string;
  registrantFirstName?: string;
  registrantLastName?: string;
  registrantEmail?: string;
  registrantPhone?: string;
  registrationDate: string;
  status: string;
  knownFrom?: string;
  specialRequirements?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

interface InvoiceGroup {
  company: string;
  invoicingCompany: string;
  invoicingAddress: string;
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  city?: string;
  invoicingStreet?: string;
  invoicingHouseNumber?: string;
  invoicingPostalCode?: string;
  invoicingCity?: string;
  courseId: string;
  courseTitle: string;
  courseLocation?: string;
  courseStartDate?: string;
  courseEndDate?: string;
  registrations: Registration[];
  totalParticipants: number;
  pricePerParticipant: number;
  discount: number;
  totalAmount: number;
  invoiceStatus: 'open' | 'invoiced' | 'paid';
  groupKey: string;
}

interface AdminInvoicesProps {
  onEditInvoice: (groupKey: string) => void;
}

const AdminInvoices: React.FC<AdminInvoicesProps> = ({ onEditInvoice }) => {
  const [registrations, setRegistrations] = useState<Registration[]>([]);
  const [invoiceGroups, setInvoiceGroups] = useState<InvoiceGroup[]>([]);
  const [deletedInvoiceGroups, setDeletedInvoiceGroups] = useState<InvoiceGroup[]>([]);
  const [courses, setCourses] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [courseFilter, setCourseFilter] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [showDeletedTable, setShowDeletedTable] = useState(false);
  const [editingGroup, setEditingGroup] = useState<string | null>(null);
  const [editingDiscount, setEditingDiscount] = useState<number>(0);
  const [hoveredGroup, setHoveredGroup] = useState<string | null>(null);
  const [hoverPosition, setHoverPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  const client = generateClient<Schema>({ authMode: 'userPool' });

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [courseFilter, statusFilter, searchTerm, sortOrder]);

  useEffect(() => {
    fetchData();
  }, []);

  // Inject search into header (same approach as AdminReferences)
  useEffect(() => {
    const container = document.getElementById('header-selector-container');
    if (container) {
      // Check if search field already exists
      let searchInput = container.querySelector('#invoicesSearch') as HTMLInputElement;
      let statusSelect = container.querySelector('#invoicesStatusFilter') as HTMLSelectElement;
      
      if (!searchInput) {
        // Create the search field and status filter
        container.innerHTML = `
          <div style="display: flex; align-items: center; gap: 12px;">
            <input
              id="invoicesSearch"
              type="text"
              placeholder="Search companies, courses, participants, dates..."
              style="padding: 8px 12px; border: 1px solid rgba(255, 255, 255, 0.3); border-radius: 6px; font-size: 14px; min-width: 250px; background: rgba(255, 255, 255, 0.95); color: #374151; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);"
            />
            <select
              id="invoicesCourseFilter"
              style="padding: 8px 12px; border: 1px solid rgba(255, 255, 255, 0.3); border-radius: 6px; font-size: 14px; min-width: 200px; background: rgba(255, 255, 255, 0.95); color: #374151; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);"
            >
              <option value="all">All Courses</option>
              ${courses.map(course => 
                `<option value="${course.id}">${course.title}${course.location ? ` - ${course.location}` : ''}</option>`
              ).join('')}
            </select>
            <select
              id="invoicesStatusFilter"
              style="padding: 8px 12px; border: 1px solid rgba(255, 255, 255, 0.3); border-radius: 6px; font-size: 14px; min-width: 120px; background: rgba(255, 255, 255, 0.95); color: #374151; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);"
            >
              <option value="all">All Status</option>
              <option value="open">Open</option>
              <option value="invoiced">Invoiced</option>
              <option value="paid">Paid</option>
            </select>
          </div>
        `;

        // Get the newly created elements
        searchInput = container.querySelector('#invoicesSearch') as HTMLInputElement;
        statusSelect = container.querySelector('#invoicesStatusFilter') as HTMLSelectElement;
        const courseSelect = container.querySelector('#invoicesCourseFilter') as HTMLSelectElement;

        // Add event listeners
        if (searchInput) {
          searchInput.addEventListener('input', (e) => {
            const value = (e.target as HTMLInputElement).value;
            setSearchTerm(value);
          });
          
          searchInput.addEventListener('focus', (e) => {
            (e.target as HTMLInputElement).style.borderColor = 'rgba(255, 255, 255, 0.6)';
            (e.target as HTMLInputElement).style.boxShadow = '0 0 0 3px rgba(255, 255, 255, 0.2)';
          });
          
          searchInput.addEventListener('blur', (e) => {
            (e.target as HTMLInputElement).style.borderColor = 'rgba(255, 255, 255, 0.3)';
            (e.target as HTMLInputElement).style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
          });
        }

        if (courseSelect) {
          courseSelect.addEventListener('change', (e) => {
            const value = (e.target as HTMLSelectElement).value;
            setCourseFilter(value);
          });
          
          courseSelect.addEventListener('focus', (e) => {
            (e.target as HTMLSelectElement).style.borderColor = 'rgba(255, 255, 255, 0.6)';
            (e.target as HTMLSelectElement).style.boxShadow = '0 0 0 3px rgba(255, 255, 255, 0.2)';
          });
          
          courseSelect.addEventListener('blur', (e) => {
            (e.target as HTMLSelectElement).style.borderColor = 'rgba(255, 255, 255, 0.3)';
            (e.target as HTMLSelectElement).style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
          });
        }

        if (statusSelect) {
          statusSelect.addEventListener('change', (e) => {
            const value = (e.target as HTMLSelectElement).value;
            setStatusFilter(value);
          });
          
          statusSelect.addEventListener('focus', (e) => {
            (e.target as HTMLSelectElement).style.borderColor = 'rgba(255, 255, 255, 0.6)';
            (e.target as HTMLSelectElement).style.boxShadow = '0 0 0 3px rgba(255, 255, 255, 0.2)';
          });
          
          statusSelect.addEventListener('blur', (e) => {
            (e.target as HTMLSelectElement).style.borderColor = 'rgba(255, 255, 255, 0.3)';
            (e.target as HTMLSelectElement).style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
          });
        }
      }
      
      // Update the input values without recreating
      if (searchInput) {
        searchInput.value = searchTerm;
      }
      if (statusSelect) {
        statusSelect.value = statusFilter;
      }
      const courseSelect = container.querySelector('#invoicesCourseFilter') as HTMLSelectElement;
      if (courseSelect) {
        courseSelect.value = courseFilter;
      }
    }

    return () => {
      // Cleanup only when component unmounts
      const container = document.getElementById('header-selector-container');
      if (container) {
        container.innerHTML = '';
      }
    };
  }, []); // Empty dependency array - only run once

  // Separate effect to update the search input values without recreating the DOM
  useEffect(() => {
    const searchInput = document.getElementById('invoicesSearch') as HTMLInputElement;
    const statusSelect = document.getElementById('invoicesStatusFilter') as HTMLSelectElement;
    const courseSelect = document.getElementById('invoicesCourseFilter') as HTMLSelectElement;
    
    if (searchInput && searchInput.value !== searchTerm) {
      searchInput.value = searchTerm;
    }
    if (statusSelect && statusSelect.value !== statusFilter) {
      statusSelect.value = statusFilter;
    }
    if (courseSelect && courseSelect.value !== courseFilter) {
      courseSelect.value = courseFilter;
    }
  }, [searchTerm, statusFilter, courseFilter]);

  // Update course options when courses are fetched
  useEffect(() => {
    const courseSelect = document.getElementById('invoicesCourseFilter') as HTMLSelectElement;
    if (courseSelect && courses.length > 0) {
      const currentValue = courseSelect.value;
      courseSelect.innerHTML = `
        <option value="all">All Courses</option>
        ${courses.map(course => 
          `<option value="${course.id}">${course.title}${course.location ? ` - ${course.location}` : ''}</option>`
        ).join('')}
      `;
      // Restore the previous selection if it still exists
      courseSelect.value = currentValue;
      console.log('✅ Updated course dropdown with', courses.length, 'courses');
    }
  }, [courses]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch ALL registrations with pagination to handle large datasets
      let allRegistrations: any[] = [];
      let nextToken: string | null | undefined = null;
      
      do {
        const response: any = await client.models.Registration.list({
          limit: 1000,
          nextToken: nextToken as any
        });
        
        allRegistrations = [...allRegistrations, ...(response.data || [])];
        nextToken = response.nextToken;
        
        console.log(`📊 Fetched ${response.data?.length || 0} registrations (total so far: ${allRegistrations.length})`);
      } while (nextToken);
      
      console.log('🔍 AdminInvoices: Total registrations fetched:', allRegistrations.length);
      console.log('📊 Registration statuses:', allRegistrations.map((r: any) => ({ 
        id: r.id, 
        status: r.status, 
        isApproved: r.isApproved,
        company: r.company 
      })));
      
      // Show ALL registrations - approval happens in the invoices page
      console.log('✅ All registrations for invoicing:', allRegistrations.length);
      setRegistrations(allRegistrations);

      // Fetch courses with only the fields we need (no status field)
      const coursesResponse = await client.models.Course.list({
        selectionSet: [
          'id', 'courseId', 'title', 'location', 'price',
          'startDate', 'endDate', 'isActive'
        ]
      });
      
      const allCourses = coursesResponse.data || [];
      
      // Filter to only show active courses in the dropdown
      const activeCourses = allCourses.filter((course: any) => course.isActive !== false);
      console.log(`📚 Filtered courses: ${activeCourses.length} active out of ${allCourses.length} total`);
      setCourses(activeCourses);

      // Group registrations for invoicing - use ALL courses so inactive course registrations still show course info
      createInvoiceGroups(allRegistrations as Registration[], allCourses);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const createInvoiceGroups = (regs: Registration[], coursesList: any[]) => {
    console.log('🏗️ Creating invoice groups from registrations:', regs.length);
    console.log('📚 Available courses:', coursesList.length);
    
    // DEBUG: Log all registration statuses to understand what we're working with
    console.log('📋 All registration statuses:', regs.map(r => ({
      id: r.id.substring(0, 8),
      company: r.company,
      status: r.status,
      regDate: r.registrationDate
    })));
    
    // CRITICAL BUG FIX: Filter out deleted registrations BEFORE grouping
    // This prevents deleted registrations from being merged with new ones
    const activeRegs = regs.filter(reg => reg.status !== 'deleted');
    const deletedRegs = regs.filter(reg => reg.status === 'deleted');
    
    console.log(`📊 Split: ${activeRegs.length} active, ${deletedRegs.length} deleted registrations`);
    console.log('✅ Active reg IDs:', activeRegs.map(r => r.id.substring(0, 8)));
    console.log('❌ Deleted reg IDs:', deletedRegs.map(r => r.id.substring(0, 8)));
    
    const groups: { [key: string]: InvoiceGroup } = {};

    // Process only ACTIVE registrations for active groups
    activeRegs.forEach(reg => {
      console.log('📝 Processing active registration:', {
        id: reg.id,
        company: reg.company,
        invoicingCompany: reg.invoicingCompany,
        courseId: reg.courseId,
        status: reg.status,
        registrantEmail: reg.registrantEmail
      });
      
      // SMART GROUPING STRATEGY:
      // - New registrations: Have exact same timestamp (milliseconds) from our fix
      // - Old registrations: Have timestamps milliseconds apart (need time window grouping)
      // 
      // Solution: Round timestamp to nearest 30 seconds + use registrant email
      // - Groups participants from same form submission (within 30 sec window)
      // - Keeps separate submissions distinct (different times or different registrants)
      // - Prevents accidental merging (different registrants, even if same time)
      let registrationTimeKey = 'no-time';
      if (reg.registrationDate) {
        const timestamp = new Date(reg.registrationDate).getTime();
        // Round to nearest 30 seconds (30000 milliseconds)
        const roundedTimestamp = Math.round(timestamp / 30000) * 30000;
        registrationTimeKey = new Date(roundedTimestamp).toISOString();
      }
      
      // Include registrant email to distinguish different people from same company
      const registrantKey = reg.registrantEmail || reg.email || 'no-registrant';
      const groupKey = `${reg.invoicingCompany || reg.company || 'Unknown'}-${reg.courseId}-${registrantKey}-${registrationTimeKey}`;
      
      console.log(`🔑 Generated groupKey for active reg ${reg.id.substring(0, 8)}: "${groupKey}"`);
      
      if (!groups[groupKey]) {
        const course = coursesList.find(c => c.id === reg.courseId);
        const pricePerParticipant = course?.price || 500; // Default price
        
        console.log('🆕 Creating new group:', groupKey, 'for course:', course?.title);
        
        // Build address string, handling empty fields
        const addressParts = [
          reg.invoicingStreet,
          reg.invoicingHouseNumber,
          reg.invoicingPostalCode,
          reg.invoicingCity
        ].filter(part => part && part.trim());
        
        const invoicingAddress = addressParts.length > 0 
          ? addressParts.join(', ')
          : 'No address provided';

        groups[groupKey] = {
          company: reg.company || 'Unknown Company',
          invoicingCompany: reg.invoicingCompany || reg.company || '—',
          invoicingAddress,
          street: reg.street,
          houseNumber: reg.houseNumber,
          postalCode: reg.postalCode,
          city: reg.city,
          invoicingStreet: reg.invoicingStreet,
          invoicingHouseNumber: reg.invoicingHouseNumber,
          invoicingPostalCode: reg.invoicingPostalCode,
          invoicingCity: reg.invoicingCity,
          courseId: reg.courseId,
          courseTitle: course?.title || 'Unknown Course',
          courseLocation: course?.location || undefined,
          courseStartDate: course?.startDate || undefined,
          courseEndDate: course?.endDate || undefined,
          registrations: [],
          totalParticipants: 0,
          pricePerParticipant,
          discount: 0,
          totalAmount: 0,
          invoiceStatus: determineInvoiceStatus(reg.status),
          groupKey
        };
      }

      groups[groupKey].registrations.push(reg);
      groups[groupKey].totalParticipants++;
      
      // Recalculate total amount with fixed discount per participant
      const discountedUnitPrice = groups[groupKey].pricePerParticipant - groups[groupKey].discount;
      groups[groupKey].totalAmount = groups[groupKey].totalParticipants * Math.max(0, discountedUnitPrice);
    });

    const activeGroupsArray = Object.values(groups);
    console.log('📦 Created active invoice groups:', activeGroupsArray.length);
    console.log('📋 Active groups summary:', activeGroupsArray.map(g => ({
      company: g.company,
      course: g.courseTitle,
      participants: g.totalParticipants,
      status: g.invoiceStatus
    })));
    
    // Now process DELETED registrations separately for the deleted table
    const deletedGroups: { [key: string]: InvoiceGroup } = {};
    
    deletedRegs.forEach(reg => {
      const registrationDateKey = reg.registrationDate 
        ? new Date(reg.registrationDate).toISOString()  // Full ISO timestamp
        : 'no-date';
      
      const groupKey = `${reg.invoicingCompany || reg.company || 'Unknown'}-${reg.courseId}-${registrationDateKey}`;
      
      if (!deletedGroups[groupKey]) {
        const course = coursesList.find(c => c.id === reg.courseId);
        const pricePerParticipant = course?.price || 500;
        
        const addressParts = [
          reg.invoicingStreet,
          reg.invoicingHouseNumber,
          reg.invoicingPostalCode,
          reg.invoicingCity
        ].filter(Boolean);
        const invoicingAddress = addressParts.join(', ') || 'No address';
        
        deletedGroups[groupKey] = {
          company: reg.company || 'Unknown Company',
          invoicingCompany: reg.invoicingCompany || reg.company || 'Unknown Company',
          invoicingAddress,
          street: reg.street,
          houseNumber: reg.houseNumber,
          postalCode: reg.postalCode,
          city: reg.city,
          invoicingStreet: reg.invoicingStreet,
          invoicingHouseNumber: reg.invoicingHouseNumber,
          invoicingPostalCode: reg.invoicingPostalCode,
          invoicingCity: reg.invoicingCity,
          courseId: reg.courseId || '',
          courseTitle: course?.title || 'Unknown Course',
          courseLocation: course?.location,
          courseStartDate: course?.startDate,
          courseEndDate: course?.endDate,
          registrations: [],
          totalParticipants: 0,
          pricePerParticipant,
          discount: 0,
          totalAmount: 0,
          invoiceStatus: 'open',
          groupKey
        };
      }
      
      deletedGroups[groupKey].registrations.push(reg);
      deletedGroups[groupKey].totalParticipants++;
      
      const discountedUnitPrice = deletedGroups[groupKey].pricePerParticipant - deletedGroups[groupKey].discount;
      deletedGroups[groupKey].totalAmount = deletedGroups[groupKey].totalParticipants * Math.max(0, discountedUnitPrice);
    });
    
    const deletedGroupsArray = Object.values(deletedGroups);
    console.log('🗑️ Created deleted invoice groups:', deletedGroupsArray.length);
    
    console.log('📊 Final counts - Active groups:', activeGroupsArray.length, 'Deleted groups:', deletedGroupsArray.length);
    setInvoiceGroups(activeGroupsArray);
    setDeletedInvoiceGroups(deletedGroupsArray);
  };

  const determineInvoiceStatus = (registrationStatus: string): 'open' | 'invoiced' | 'paid' => {
    switch (registrationStatus) {
      case 'confirmed': return 'paid';
      case 'invoiced': return 'invoiced';
      case 'approved': return 'open';
      default: return 'open'; // New registrations start as 'open' for invoicing
    }
  };

  const updateGroupStatus = async (group: InvoiceGroup, newStatus: 'open' | 'invoiced' | 'paid') => {
    console.log('🔄 AdminInvoices: updateGroupStatus called', {
      groupKey: group.groupKey,
      newStatus,
      registrationsCount: group.registrations.length
    });
    
    try {
      // Update all registrations in this group
      const updatePromises = group.registrations.map(reg => {
        let registrationStatus = reg.status;
        
        console.log('📝 Before update:', {
          id: reg.id,
          currentStatus: reg.status,
          newStatus
        });
        
        switch (newStatus) {
          case 'invoiced':
            registrationStatus = 'invoiced';
            break;
          case 'paid':
            registrationStatus = 'confirmed'; // Paid invoices confirm the registration
            break;
          case 'open':
            registrationStatus = 'approved'; // Reset to approved
            break;
        }
        
        console.log('📝 After update logic:', {
          id: reg.id,
          registrationStatus
        });
        
        return client.models.Registration.update({
          id: reg.id,
          status: registrationStatus
        });
      });
      
      console.log('⏳ Executing updates...');
      const results = await Promise.all(updatePromises);
      console.log('✅ Update results:', results);
      
      // Check if any updates failed
      const failedUpdates = results.filter(result => !result || result.errors);
      if (failedUpdates.length > 0) {
        console.error('❌ Some updates failed:', failedUpdates);
        console.error('❌ Failed update details:');
        failedUpdates.forEach((failed, index) => {
          console.error(`Failed update ${index + 1}:`, failed);
          if (failed && failed.errors) {
            console.error(`Errors for update ${index + 1}:`, failed.errors);
          }
        });
        alert('Some updates failed. Please check the console for details.');
        return;
      }
      
      console.log('🔄 Refreshing data...');
      await fetchData(); // Refresh data
      console.log('✅ Data refresh completed');

      // Also refresh participants/registrations view if it is open
      try {
        const maybeRefresh = (window as any).adminRegistrationsRefresh;
        if (typeof maybeRefresh === 'function') {
          console.log('🔁 Triggering AdminRegistrations refresh after status update');
          maybeRefresh();
        }
      } catch (e) {
        // no-op if window not available
      }
      
      // Show success message
      console.log('🎉 Status update successful!');
      
    } catch (error) {
      console.error('❌ Error updating group status:', error);
      console.error('Error details:', error instanceof Error ? error.message : String(error));
      alert(`Error updating status: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const updateDiscount = async (group: InvoiceGroup, discount: number) => {
    try {
      // In a real implementation, you might store discount in a separate field
      // For now, we'll just update the local state and recalculate with fixed discount per participant
      const updatedGroups = invoiceGroups.map(g => {
        if (g.groupKey === group.groupKey) {
          const discountedUnitPrice = g.pricePerParticipant - discount;
          return {
            ...g,
            discount,
            totalAmount: g.totalParticipants * Math.max(0, discountedUnitPrice)
          };
        }
        return g;
      });
      
      setInvoiceGroups(updatedGroups);
      setEditingGroup(null);
    } catch (error) {
      console.error('Error updating discount:', error);
    }
  };

  const deleteInvoiceGroup = async (group: InvoiceGroup) => {
    try {
      console.log('🗑️ Deleting invoice group:', group.groupKey);
      
      // Update all registrations in this group to 'deleted' status
      const updatePromises = group.registrations.map(reg => {
        return client.models.Registration.update({
          id: reg.id,
          status: 'deleted'
        });
      });
      
      await Promise.all(updatePromises);
      console.log('✅ Successfully deleted invoice group');
      
      // Refresh data to update the UI
      fetchData();

      // Also refresh participants/registrations view if it is open
      try {
        const maybeRefresh = (window as any).adminRegistrationsRefresh;
        if (typeof maybeRefresh === 'function') {
          console.log('🔁 Triggering AdminRegistrations refresh after deletion');
          maybeRefresh();
        }
      } catch (e) {
        // no-op
      }
    } catch (error) {
      console.error('Error deleting invoice group:', error);
    }
  };

  const restoreInvoiceGroup = async (group: InvoiceGroup) => {
    try {
      console.log('🔄 Restoring invoice group:', group.groupKey);
      
      // Update all registrations in this group back to 'approved' status
      const updatePromises = group.registrations.map(reg => {
        return client.models.Registration.update({
          id: reg.id,
          status: 'approved'
        });
      });
      
      await Promise.all(updatePromises);
      console.log('✅ Successfully restored invoice group');
      
      // Refresh data to update the UI
      fetchData();

      // Also refresh participants/registrations view if it is open
      try {
        const maybeRefresh = (window as any).adminRegistrationsRefresh;
        if (typeof maybeRefresh === 'function') {
          console.log('🔁 Triggering AdminRegistrations refresh after restore');
          maybeRefresh();
        }
      } catch (e) {
        // no-op
      }
    } catch (error) {
      console.error('Error restoring invoice group:', error);
    }
  };

  // Pagination logic
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const filteredGroups = invoiceGroups
    .filter(group => {
      // Enhanced search: company, course, participants, and dates
      const searchLower = searchTerm.toLowerCase();
      
      // Search in company/course info
      const matchesCompany = 
        group.company.toLowerCase().includes(searchLower) ||
        group.invoicingCompany.toLowerCase().includes(searchLower) ||
        group.courseTitle.toLowerCase().includes(searchLower);
      
      // Search in participant names and emails
      const matchesParticipants = group.registrations.some(reg => 
        (reg.firstName && reg.firstName.toLowerCase().includes(searchLower)) ||
        (reg.lastName && reg.lastName.toLowerCase().includes(searchLower)) ||
        (reg.email && reg.email.toLowerCase().includes(searchLower)) ||
        (reg.registrantFirstName && reg.registrantFirstName.toLowerCase().includes(searchLower)) ||
        (reg.registrantLastName && reg.registrantLastName.toLowerCase().includes(searchLower)) ||
        (reg.registrantEmail && reg.registrantEmail.toLowerCase().includes(searchLower))
      );
      
      // Search in registration dates (formatted as DD.MM.YYYY)
      const matchesDates = group.registrations.some(reg => {
        if (!reg.registrationDate) return false;
        const date = new Date(reg.registrationDate);
        const formatted = date.toLocaleDateString('de-DE'); // e.g., "08.01.2026"
        return formatted.includes(searchLower);
      });
      
      const matchesSearch = matchesCompany || matchesParticipants || matchesDates;
      const matchesStatus = statusFilter === 'all' || group.invoiceStatus === statusFilter;
      const matchesCourse = courseFilter === 'all' || group.courseId === courseFilter;
      
      return matchesSearch && matchesStatus && matchesCourse;
    })
    .sort((a, b) => {
      const dateA = new Date(a.registrations[0].registrationDate).getTime();
      const dateB = new Date(b.registrations[0].registrationDate).getTime();
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

  // Pagination calculations
  const totalItems = filteredGroups.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedGroups = filteredGroups.slice(startIndex, endIndex);

  const getStatusColor = (status: 'open' | 'invoiced' | 'paid') => {
    switch (status) {
      case 'open': return { bg: '#fef3c7', color: '#92400e' }; // Yellow like pending
      case 'invoiced': return { bg: '#dbeafe', color: '#1e40af' }; // Blue
      case 'paid': return { bg: '#e6f7f7', color: '#008F99' }; // Brand color for paid
      default: return { bg: '#f3f4f6', color: '#374151' }; // Gray
    }
  };

  if (loading) {
    return <div className="admin-loading">Loading invoices...</div>;
  }

  return (
    <div className="admin-invoices" style={{ paddingBottom: '60px', minHeight: 'calc(100vh - 77px)', display: 'flex', flexDirection: 'column' }}>
      {/* Invoice Groups Table */}
      <div className="invoice-groups-table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th 
                onClick={() => setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest')}
                style={{ cursor: 'pointer' }}
              >
                Date {sortOrder === 'newest' ? '↓' : '↑'}
              </th>
              <th>Company</th>
              <th>Course</th>
              <th>Participants</th>
              <th>Unit Price</th>
              <th>Discount</th>
              <th>Total Amount</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedGroups.map(group => (
              <tr key={group.groupKey}>
                <td
                  onMouseEnter={(e) => {
                    setHoveredGroup(group.groupKey);
                    setHoverPosition({ x: e.clientX, y: e.clientY });
                  }}
                  onMouseLeave={() => setHoveredGroup(null)}
                  onMouseMove={(e) => {
                    if (hoveredGroup === group.groupKey) {
                      setHoverPosition({ x: e.clientX, y: e.clientY });
                    }
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <div>
                    <div style={{
                      fontSize: '12px',
                      fontWeight: 'bold',
                      color: '#374151'
                    }}>
                      {new Date(group.registrations[0].registrationDate).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#374151'
                    }}>
                      {new Date(group.registrations[0].registrationDate).toLocaleTimeString('de-DE', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                  </div>
                </td>
                <td>
                  <div>
                    <div><strong>{group.company}</strong></div>
                    <div className="text-sm text-gray">
                      {[group.street, group.houseNumber].filter(Boolean).join(' ') || 'No street address'}
                    </div>
                    <div className="text-sm text-gray">
                      {[group.postalCode, group.city].filter(Boolean).join(' ') || 'No city'}
                    </div>
                    {group.registrations.length > 0 && (
                      <>
                        <div className="text-sm" style={{ marginTop: '4px' }}>
                          {[group.registrations[0].registrantFirstName, group.registrations[0].registrantLastName].filter(Boolean).join(' ') || 'No registrant name'}
                        </div>
                        <div className="text-sm text-gray">
                          {group.registrations[0].registrantEmail || group.registrations[0].email || 'No email available'}
                        </div>
                      </>
                    )}
                  </div>
                </td>
                <td>
                  <div>
                    <div style={{ fontWeight: 'bold' }}>{group.courseTitle}</div>
                    {group.courseStartDate && (
                      <div className="text-sm text-gray">
                        | {formatDateToDDMMYY(group.courseStartDate)}{group.courseEndDate ? ` - ${formatDateToDDMMYY(group.courseEndDate)}` : ''} |
                      </div>
                    )}
                    {group.courseLocation && (
                      <div className="text-sm text-gray">{group.courseLocation}</div>
                    )}
                  </div>
                </td>
                <td>
                  <div>
                    <strong>{group.totalParticipants} participants</strong>
                    <div className="text-sm">
                      {group.registrations
                        .map(reg => `${reg.firstName} ${reg.lastName}`)
                        .reduce((acc, name, index) => {
                          if (index % 3 === 0) {
                            acc.push([name]);
                          } else {
                            acc[acc.length - 1].push(name);
                          }
                          return acc;
                        }, [] as string[][])
                        .map((line, lineIndex) => (
                          <div key={lineIndex}>{line.join(', ')}</div>
                        ))
                      }
                    </div>
                  </div>
                </td>
                <td className="text-right">
                  {formatGermanNumber(group.pricePerParticipant)}
                </td>
                <td className="text-right">
                  {editingGroup === group.groupKey ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                      <input
                        type="number"
                        value={editingDiscount}
                        onChange={(e) => setEditingDiscount(Number(e.target.value))}
                        ref={(el) => {
                          if (el) {
                            el.style.setProperty('background-color', '#e6f7f8', 'important');
                          }
                        }}
                        style={{
                          width: '60px',
                          padding: '2px 6px',
                          border: '1px solid #008F99',
                          borderRadius: '4px',
                          fontSize: '14px',
                          textAlign: 'right',
                          outline: 'none',
                          boxShadow: 'none'
                        }}
                        min="0"
                        step="1"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            updateDiscount(group, editingDiscount);
                          } else if (e.key === 'Escape') {
                            setEditingGroup(null);
                          }
                        }}
                        onBlur={() => {
                          // Auto-save on blur after a short delay
                          setTimeout(() => {
                            if (editingGroup === group.groupKey) {
                              updateDiscount(group, editingDiscount);
                            }
                          }, 150);
                        }}
                      />
                    </div>
                  ) : (
                    <div 
                      style={{
                        cursor: 'pointer',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        backgroundColor: '#e6f7f8',
                        transition: 'background-color 0.2s ease'
                      }}
                      onClick={() => {
                        setEditingGroup(group.groupKey);
                        setEditingDiscount(group.discount);
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#d1f2f4';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#e6f7f8';
                      }}
                      title="Click to edit discount"
                    >
                      {group.discount}
                    </div>
                  )}
                </td>
                <td className="text-right">
                  <strong>{formatGermanNumber(group.totalAmount)}</strong>
                </td>
                <td>
                  <span style={{
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 500,
                    textTransform: 'uppercase',
                    backgroundColor: getStatusColor(group.invoiceStatus).bg,
                    color: getStatusColor(group.invoiceStatus).color
                  }}>
                    {group.invoiceStatus}
                  </span>
                </td>
                <td>
                  <div className="actions" style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                    <button
                      onClick={() => {
                        console.log('🔗 AdminInvoices: Edit clicked for group:', group.groupKey);
                        console.log('🔗 AdminInvoices: Group details:', { 
                          company: group.company, 
                          invoicingCompany: group.invoicingCompany, 
                          courseId: group.courseId,
                          courseTitle: group.courseTitle 
                        });
                        onEditInvoice(group.groupKey);
                      }}
                      style={{
                        padding: '4px 8px',
                        border: '1px solid #008F99',
                        borderRadius: '4px',
                        background: 'white',
                        color: '#008F99',
                        fontSize: '11px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#008F99';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.color = '#008F99';
                      }}
                    >
                      Edit
                    </button>
                    
                    <button
                      onClick={() => {
                        const registrationIds = group.registrations.map(reg => reg.id).join('\n');
                        navigator.clipboard.writeText(registrationIds).then(() => {
                          // Optional: Show a temporary success message
                          const btn = document.activeElement as HTMLButtonElement;
                          const originalText = btn.textContent;
                          btn.textContent = '✓ Copied!';
                          setTimeout(() => {
                            btn.textContent = originalText;
                          }, 1500);
                        }).catch(err => {
                          console.error('Failed to copy registration IDs:', err);
                          alert('Failed to copy to clipboard');
                        });
                      }}
                      style={{
                        padding: '4px 8px',
                        border: '1px solid #6b7280',
                        borderRadius: '4px',
                        background: 'white',
                        color: '#6b7280',
                        fontSize: '11px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#6b7280';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.color = '#6b7280';
                      }}
                    >
                      Copy IDs
                    </button>
                    
                    {group.invoiceStatus === 'open' && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('🔘 INVOICED BUTTON CLICKED!', group.groupKey);
                          updateGroupStatus(group, 'invoiced');
                        }}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid #008F99',
                          borderRadius: '4px',
                          background: 'white',
                          color: '#008F99',
                          fontSize: '11px',
                          cursor: 'pointer',
                          marginRight: '8px',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#008F99';
                          e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'white';
                          e.currentTarget.style.color = '#008F99';
                        }}
                      >
                        Invoiced
                      </button>
                    )}
                    
                    {group.invoiceStatus === 'invoiced' && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('🔘 PAID BUTTON CLICKED!', group.groupKey);
                          updateGroupStatus(group, 'paid');
                        }}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid #008F99',
                          borderRadius: '4px',
                          background: 'white',
                          color: '#008F99',
                          fontSize: '11px',
                          cursor: 'pointer',
                          marginRight: '8px',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#008F99';
                          e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'white';
                          e.currentTarget.style.color = '#008F99';
                        }}
                      >
                        Paid
                      </button>
                    )}

                    {group.invoiceStatus === 'paid' && (
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          console.log('🔙 REVERT TO INVOICED BUTTON CLICKED!', group.groupKey);
                          updateGroupStatus(group, 'invoiced');
                        }}
                        style={{
                          padding: '4px 8px',
                          border: '1px solid #008F99',
                          borderRadius: '4px',
                          background: 'white',
                          color: '#008F99',
                          fontSize: '11px',
                          cursor: 'pointer',
                          marginRight: '8px',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#008F99';
                          e.currentTarget.style.color = 'white';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'white';
                          e.currentTarget.style.color = '#008F99';
                        }}
                      >
                        ← Invoiced
                      </button>
                    )}

                    
                    <button
                      onClick={() => deleteInvoiceGroup(group)}
                      style={{
                        padding: '4px 8px',
                        border: '1px solid #dc2626',
                        borderRadius: '4px',
                        background: 'white',
                        color: '#dc2626',
                        fontSize: '11px',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#dc2626';
                        e.currentTarget.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'white';
                        e.currentTarget.style.color = '#dc2626';
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {paginatedGroups.length === 0 && (
          <div className="no-data">
            {searchTerm || statusFilter !== 'all' 
              ? 'No invoice groups match your search criteria.' 
              : 'No approved registrations found for invoicing.'}
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {!loading && totalPages > 1 && (
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ fontSize: '14px', color: '#6b7280' }}>
            Showing {startIndex + 1}-{Math.min(endIndex, totalItems)} of {totalItems} invoice groups
          </div>
          
          <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              style={{
                padding: '6px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                backgroundColor: currentPage === 1 ? '#f3f4f6' : 'white',
                color: currentPage === 1 ? '#9ca3af' : '#374151',
                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              Previous
            </button>
            
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '4px',
                    backgroundColor: currentPage === pageNum ? '#008F99' : 'white',
                    color: currentPage === pageNum ? 'white' : '#374151',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: currentPage === pageNum ? '600' : '400'
                  }}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              style={{
                padding: '6px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                backgroundColor: currentPage === totalPages ? '#f3f4f6' : 'white',
                color: currentPage === totalPages ? '#9ca3af' : '#374151',
                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                fontSize: '14px'
              }}
            >
              Next
            </button>
          </div>
        </div>
      )}
      
      {/* Pagination Info for single page */}
      {!loading && totalPages <= 1 && totalItems > 0 && (
        <div style={{
          padding: '12px 24px',
          borderTop: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          fontSize: '14px',
          color: '#6b7280',
          textAlign: 'center'
        }}>
          Showing all {totalItems} invoice groups
        </div>
      )}

      {/* Deleted Invoice Groups Section */}
      {!loading && (
        <div className="admin-content-card" style={{ marginTop: '32px' }}>
          <div 
            onClick={() => setShowDeletedTable(!showDeletedTable)}
            style={{
              padding: '16px 24px',
              backgroundColor: '#f9fafb',
              borderBottom: showDeletedTable ? '1px solid #e5e7eb' : 'none',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{ fontSize: '16px', fontWeight: '600', color: '#374151' }}>
                Deleted Invoice Groups ({deletedInvoiceGroups.length})
              </span>
              <span style={{ fontSize: '12px', color: '#6b7280' }}>
                Click to {showDeletedTable ? 'hide' : 'show'}
              </span>
            </div>
            <span style={{ fontSize: '18px', color: '#6b7280', transform: showDeletedTable ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
              ▼
            </span>
          </div>
          
          {showDeletedTable && (
            <div style={{ overflowX: 'auto' }}>
              {deletedInvoiceGroups.length === 0 ? (
                <div style={{ 
                  padding: '48px 24px', 
                  textAlign: 'center', 
                  color: '#6b7280',
                  backgroundColor: '#fefefe'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>🗑️</div>
                  <h3 style={{ margin: '0 0 8px 0', color: '#374151' }}>No deleted invoice groups</h3>
                  <p style={{ margin: 0 }}>
                    When you delete invoice groups, they will appear here and can be restored.
                  </p>
                </div>
              ) : (
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Company</th>
                      <th>Course</th>
                      <th>Participants</th>
                      <th>Unit Price</th>
                      <th>Discount</th>
                      <th>Total Amount</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deletedInvoiceGroups.map(group => (
                      <tr key={group.groupKey} style={{ opacity: 0.6 }}>
                        <td>
                          <div>
                            <div style={{
                              fontSize: '12px',
                              fontWeight: 'bold',
                              color: '#374151'
                            }}>
                              {new Date(group.registrations[0].registrationDate).toLocaleDateString('de-DE', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              })}
                            </div>
                            <div style={{
                              fontSize: '12px',
                              color: '#374151'
                            }}>
                              {new Date(group.registrations[0].registrationDate).toLocaleTimeString('de-DE', {
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </div>
                          </div>
                        </td>
                        <td>
                          <div>
                            <div><strong>{group.company}</strong></div>
                            <div className="text-sm text-gray">
                              {[group.street, group.houseNumber].filter(Boolean).join(' ') || 'No street address'}
                            </div>
                            <div className="text-sm text-gray">
                              {[group.postalCode, group.city].filter(Boolean).join(' ') || 'No city'}
                            </div>
                            {group.registrations.length > 0 && (
                              <>
                                <div className="text-sm" style={{ marginTop: '4px' }}>
                                  {[group.registrations[0].registrantFirstName, group.registrations[0].registrantLastName].filter(Boolean).join(' ') || 'No registrant name'}
                                </div>
                                <div className="text-sm text-gray">
                                  {group.registrations[0].registrantEmail || group.registrations[0].email || 'No email available'}
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                        <td>
                          <div>
                            <div style={{ fontWeight: 'bold' }}>{group.courseTitle}</div>
                            {group.courseStartDate && (
                              <div className="text-sm text-gray">
                                | {formatDateToDDMMYY(group.courseStartDate)}{group.courseEndDate ? ` - ${formatDateToDDMMYY(group.courseEndDate)}` : ''} |
                              </div>
                            )}
                            {group.courseLocation && (
                              <div className="text-sm text-gray">{group.courseLocation}</div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div>
                            <strong>{group.totalParticipants} participants</strong>
                            <div className="text-sm">
                              {group.registrations
                                .map(reg => `${reg.firstName} ${reg.lastName}`)
                                .reduce((acc, name, index) => {
                                  if (index % 3 === 0) {
                                    acc.push([name]);
                                  } else {
                                    acc[acc.length - 1].push(name);
                                  }
                                  return acc;
                                }, [] as string[][])
                                .map((line, lineIndex) => (
                                  <div key={lineIndex}>{line.join(', ')}</div>
                                ))
                              }
                            </div>
                          </div>
                        </td>
                        <td className="text-right">
                          {formatGermanNumber(group.pricePerParticipant)}
                        </td>
                        <td className="text-right">
                          <div style={{
                            padding: '2px 6px',
                            borderRadius: '4px',
                            backgroundColor: '#e6f7f8'
                          }}>
                            {group.discount}
                          </div>
                        </td>
                        <td className="text-right">
                          <strong>{formatGermanNumber(group.totalAmount)}</strong>
                        </td>
                        <td>
                          <span style={{
                            padding: '4px 8px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 500,
                            textTransform: 'uppercase',
                            backgroundColor: getStatusColor(group.invoiceStatus).bg,
                            color: getStatusColor(group.invoiceStatus).color
                          }}>
                            {group.invoiceStatus}
                          </span>
                        </td>
                        <td>
                          <div className="actions" style={{ display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
                            <button
                              onClick={() => restoreInvoiceGroup(group)}
                              style={{
                                padding: '4px 8px',
                                border: '1px solid #008F99',
                                borderRadius: '4px',
                                background: 'white',
                                color: '#008F99',
                                fontSize: '11px',
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = '#008F99';
                                e.currentTarget.style.color = 'white';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'white';
                                e.currentTarget.style.color = '#008F99';
                              }}
                            >
                              Restore
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      {/* Hover Tooltip */}
      {hoveredGroup && (
        <div
          style={{
            position: 'fixed',
            left: Math.min(hoverPosition.x + 10, window.innerWidth - Math.min(window.innerWidth * 0.9, 600)),
            top: hoverPosition.y > window.innerHeight / 2 
              ? Math.max(20, hoverPosition.y - 400)
              : hoverPosition.y + 10,
            background: 'rgba(0, 0, 0, 0.9)',
            color: 'white',
            padding: '16px 20px',
            borderRadius: '8px',
            fontSize: '13px',
            maxWidth: '90vw',
            maxHeight: '80vh',
            overflowY: 'auto',
            zIndex: 1000,
            pointerEvents: 'none',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            lineHeight: '1.6'
          }}
        >
          {(() => {
            const group = invoiceGroups.find(g => g.groupKey === hoveredGroup);
            if (!group) return null;
            
            return (
              <div>
                <div style={{ fontWeight: 'bold', marginBottom: '12px', color: '#008F99', fontSize: '14px' }}>
                  📋 Registration Details ({group.totalParticipants} Participant{group.totalParticipants > 1 ? 's' : ''})
                </div>
                
                <div style={{ 
                  display: 'flex',
                  gap: '16px',
                  flexWrap: 'nowrap',
                  overflowX: 'auto',
                  marginBottom: '12px'
                }}>
                  {group.registrations.map((reg, index) => (
                    <div key={reg.id} style={{ 
                      flex: group.registrations.length > 1 ? '0 0 250px' : '1 1 auto',
                      minWidth: group.registrations.length > 1 ? '250px' : 'auto',
                      paddingRight: group.registrations.length > 1 && index < group.registrations.length - 1 ? '16px' : '0',
                      borderRight: group.registrations.length > 1 && index < group.registrations.length - 1 ? '1px solid rgba(255,255,255,0.2)' : 'none'
                    }}>
                      {group.registrations.length > 1 && (
                        <div style={{ fontWeight: 'bold', marginBottom: '8px', color: '#60a5fa' }}>
                          Participant {index + 1}
                        </div>
                      )}
                      
                      <div style={{ marginBottom: '8px' }}>
                        <strong>👤 Participant:</strong><br />
                        {reg.firstName} {reg.lastName}
                        {reg.email && <><br />✉️ {reg.email}</>}
                        {reg.phone && <><br />📞 {reg.phone}</>}
                      </div>
                      
                      {(reg.registrantFirstName || reg.registrantLastName || reg.registrantEmail) && (
                        <div style={{ marginBottom: '8px' }}>
                          <strong>📝 Registrant:</strong><br />
                          {[reg.registrantFirstName, reg.registrantLastName].filter(Boolean).join(' ')}
                          {reg.registrantEmail && <><br />✉️ {reg.registrantEmail}</>}
                          {reg.registrantPhone && <><br />📞 {reg.registrantPhone}</>}
                        </div>
                      )}
                      
                      {reg.company && (
                        <div style={{ marginBottom: '8px' }}>
                          <strong>🏢 Company:</strong><br />
                          {reg.company}
                          {reg.website && <><br />🌐 {reg.website}</>}
                          {(reg.street || reg.houseNumber) && <><br />{[reg.street, reg.houseNumber].filter(Boolean).join(' ')}</>}
                          {(reg.postalCode || reg.city) && <><br />{[reg.postalCode, reg.city].filter(Boolean).join(' ')}</>}
                        </div>
                      )}
                      
                      {(reg.invoicingCompany || reg.invoicingStreet || reg.invoicingCity) && (
                        <div style={{ marginBottom: '8px' }}>
                          <strong>💼 Invoicing Address:</strong><br />
                          {reg.invoicingCompany}
                          {(reg.invoicingStreet || reg.invoicingHouseNumber) && <><br />{[reg.invoicingStreet, reg.invoicingHouseNumber].filter(Boolean).join(' ')}</>}
                          {(reg.invoicingPostalCode || reg.invoicingCity) && <><br />{[reg.invoicingPostalCode, reg.invoicingCity].filter(Boolean).join(' ')}</>}
                        </div>
                      )}
                      
                      <div style={{ marginBottom: '4px' }}>
                        <strong>📅 Registration Date:</strong><br />
                        {new Date(reg.registrationDate).toLocaleDateString('de-DE', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </div>
                      
                      <div style={{ marginBottom: '4px' }}>
                        <strong>📊 Status:</strong> {reg.status === 'confirmed' ? 'Paid' : reg.status}
                      </div>
                      
                      {reg.knownFrom && (
                        <div style={{ marginBottom: '4px' }}>
                          <strong>🔍 Known From:</strong> {reg.knownFrom}
                        </div>
                      )}
                      
                      {reg.specialRequirements && (
                        <div style={{ marginBottom: '4px' }}>
                          <strong>⚠️ Special Requirements:</strong><br />
                          {reg.specialRequirements}
                        </div>
                      )}
                      
                      {reg.notes && (
                        <div style={{ marginBottom: '4px' }}>
                          <strong>📝 Notes:</strong><br />
                          {reg.notes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                
                <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.2)' }}>
                  <strong>🎓 Course:</strong> {group.courseTitle}
                  {group.courseStartDate && (
                    <>
                      <br /><strong>📅 Start:</strong> {formatDateToDDMMYY(group.courseStartDate)}
                    </>
                  )}
                  {group.courseLocation && (
                    <>
                      <br /><strong>📍 Location:</strong> {group.courseLocation}
                    </>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Blank footer */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: '280px',
        right: 0,
        background: 'white',
        borderTop: '1px solid #e5e7eb',
        boxShadow: '0 -4px 10px rgba(0, 0, 0, 0.06)',
        zIndex: 200,
        padding: '12px 24px',
        height: '48px'
      }} />
    </div>
  );
};

export default AdminInvoices;
